<template>
  <div class="video-generation">
    <keep-alive>
      <ImageGeneration
        v-if="currentStep === 'source'"
        @next="handleSourceNext"
        @image-state-change="handleImageSourceStateChange"
      />

      <VideoFormGeneration
        v-else-if="currentStep === 'form'"
        @go-back-to-selection="handleGoBackToSource"
        @generate-form="handleGenerateForm"
      />

      <VideoCreationPolling
        v-else-if="currentStep === 'polling' && currentTaskId"
        :task-id="currentTaskId"
        @proceed-to-next="handlePollingComplete"
      />
    </keep-alive>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ImageGeneration from './components/ImageGeneration.vue'
import VideoFormGeneration from './components/VideoFormGeneration.vue'
import VideoCreationPolling from './components/VideoCreationPolling.vue'

export default {
  name: 'VideoGenerationV2',
  components: {
    ImageGeneration,
    VideoFormGeneration,
    VideoCreationPolling,
  },
  data() {
    return {
      currentStep: 'source', // 'source', 'form', 'polling'
      currentTaskId: null,
      hasImageSource: false,
    }
  },
  created() {
    this.clearSelectedVideos()
  },
  methods: {
    ...mapActions('videoGeneration', ['setSelectedVideos', 'clearSelectedVideos']),

    handleSourceNext(selectedVideos) {
      const videos = Array.isArray(selectedVideos) ? selectedVideos : []
      if (!videos.length) {
        this.$toast.warning('Please select at least one video to proceed.')
        return
      }

      this.setSelectedVideos(videos)
      this.currentTaskId = null
      this.currentStep = 'form'

      // Scroll to top when navigating to form step
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      })
    },

    handleGoBackToSource() {
      // Don't clear selected videos - keep-alive will preserve component state
      this.currentTaskId = null
      this.currentStep = 'source'

      // Scroll to top when going back
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      })
    },

    handleGenerateForm(result) {
      const taskId = this.extractTaskId(result)
      if (!taskId) {
        if (this.$toast && typeof this.$toast.error === 'function') {
          this.$toast.error('No task ID received from form generation')
        }
        return
      }

      this.currentTaskId = taskId
      this.currentStep = 'polling'
    },

    handlePollingComplete() {
      if (this.$toast && typeof this.$toast.success === 'function') {
        this.$toast.success('Video creation process completed!')
      }

      let query = {}
      if (this.$route && this.$route.query) {
        query = { ...this.$route.query }
      } else {
        try {
          const url = new URL(window.location.href)
          query = Object.fromEntries(url.searchParams.entries())
        } catch (error) {
          query = {}
        }
      }

      if (!this.$route?.query?.advertiserId) {
        this.$router.push({ path: '/generated-library' })
        return
      }

      this.$router.push({ path: '/campaign/duplicate', query })
    },

    extractTaskId(payload) {
      if (!payload) return null

      const queue = [payload]
      const visited = new Set()

      while (queue.length) {
        const item = queue.shift()
        if (item) {
          if (typeof item === 'string' || typeof item === 'number') {
            // Primitive task identifiers (if provided directly)
            const value = String(item).trim()
            if (value) return value
          } else if (typeof item === 'object' && !visited.has(item)) {
            visited.add(item)

            if (item.taskId || item.task_id) return item.taskId || item.task_id

            const values = Array.isArray(item) ? item : Object.values(item)
            values.forEach(value => {
              queue.push(value)
            })
          }
        }
      }

      return null
    },

    handleImageSourceStateChange(isActive) {
      this.hasImageSource = Boolean(isActive)
    },
  },
}
</script>

<style scoped>
.video-generation {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.video-generation__library {
  margin-top: 8px;
}
</style>
